/**
 * The receipt of a change: what a version added, changed and left out, worked
 * out from the two snapshots alone (J2.8), so a typed turn, a call, a canvas
 * edit and a second tab all show the same one. Structured, with no words: the
 * client words each line with `setup.studio.receipt.*` ("Added {{what}}",
 * "Changed {{what}}", "Left out {{what}}", "Back to version {{n}}").
 *
 * Parts are matched by key, never by position. A gap newly left is one
 * `changed` line of kind `gap` ("Left as it is"); a critic's check and
 * provenance make none (the critic's version carries its own reason).
 */
import type { SetupDraftField } from '../compose-author';
import { LANGUAGE_PICKED_GAP, type StudioDraft, type StudioRecord } from './draft';
import { studioPathsMeet } from './paths';

/** `gap`: a gap the person chose to leave, worded "Left as it is". */
export type StudioPartKind = 'workspace' | 'record' | 'list' | 'field' | 'link' | 'card' | 'board' | 'role' | 'import' | 'source' | 'note' | 'not_yet' | 'gap';

export type StudioReceiptLine =
  | { op: 'added'; kind: StudioPartKind; path: string; name: string; on?: string; to?: string; fields?: number; rows?: number }
  | {
      op: 'changed';
      kind: StudioPartKind;
      path: string;
      name: string;
      on?: string;
      to?: string;
      renamedFrom?: string;
      addedFields?: string[];
      removedFields?: string[];
      changedFields?: string[];
    }
  | { op: 'left'; kind: StudioPartKind; path: string; name: string; on?: string; to?: string }
  | { op: 'decided'; path: string; text: string }
  | { op: 'not_yet'; path: string; name: string; fit: string }
  /** `n`: the version's number as the person counts them (`studioShownVersion`), when the store gave one. */
  | { op: 'restored'; version: number; n?: number };

export type StudioActor = 'agent' | 'person' | 'system';
export type StudioHow = 'start' | 'turn' | 'call' | 'canvas' | 'check' | 'restore';

/**
 * One version's receipt, as the store keeps it on the version row. `ring` is every line's path: what the canvas rings.
 *
 * One version per message (Devendra, 16 Oct): a turn's compile commits each
 * pass as it lands, so the canvas draws as it goes, but the passes of one
 * turn are steps of one version. Each pass's receipt says everything the turn
 * did so far (its lines run from `from`), and all of them carry the same `n`,
 * the version's number as the person counts them: one a message, one a
 * direct edit, one a restore. The turn's last pass is the version listed; the
 * ones before it are steps, never listed, never gone back to.
 */
export type StudioReceipt = {
  version: number;
  /** The number the person sees ("Version 4"): the listed versions counted 1, 2, 3… with the Start 0, which the store sets on every receipt it answers from the history (`studioVersionNumbers`). */
  n?: number;
  /** The version this receipt's lines start from, which Undo goes back to; absent before 16 Oct (`version - 1`). */
  from?: number;
  actor: StudioActor;
  how: StudioHow;
  at: string;
  because: string | null;
  scope: string[] | null;
  restoredFrom: number | null;
  lines: StudioReceiptLine[];
  ring: string[];
};

const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);
const byKey = <T extends { key: string }>(list: readonly T[]): Map<string, T> => new Map(list.map((one) => [one.key, one]));
const recordKind = (record: StudioRecord): StudioPartKind => (record.kind === 'directory' ? 'list' : 'record');
const withoutTiles = ({ tiles: _tiles, ...rest }: StudioRecord): Omit<StudioRecord, 'tiles'> => rest;

/** The lines that turn `before` into `after`: added, changed, left, decided, not yet; each in draft order. */
export const studioReceiptLines = (before: StudioDraft, after: StudioDraft): StudioReceiptLine[] => {
  const added: StudioReceiptLine[] = [];
  const changed: StudioReceiptLine[] = [];
  const left: StudioReceiptLine[] = [];
  const decided: StudioReceiptLine[] = [];
  const notYet: StudioReceiptLine[] = [];
  const recordName = (draft: StudioDraft, key: string): string => draft.records.find((one) => one.key === key)?.name ?? '';

  // The workspace.
  if (!same(before.meta, after.meta)) {
    changed.push({
      op: 'changed',
      kind: 'workspace',
      path: 'meta',
      name: after.meta.name,
      ...(before.meta.name === after.meta.name ? {} : { renamedFrom: before.meta.name }),
    });
  }

  // Records and lists, and each one's own cards.
  const was = byKey(before.records);
  const now = byKey(after.records);
  for (const record of after.records) {
    const path = `record:${record.key}`;
    const old = was.get(record.key);
    if (old === undefined) {
      added.push({
        op: 'added',
        kind: recordKind(record),
        path,
        name: record.name,
        fields: record.fields.length,
        ...(record.kind === 'directory' ? { rows: record.seedRows?.length ?? 0 } : {}),
      });
      continue;
    }
    if (!same(withoutTiles(old), withoutTiles(record))) {
      const oldFields = byKey<SetupDraftField>(old.fields);
      const newFields = byKey<SetupDraftField>(record.fields);
      const addedFields = record.fields.filter((field) => !oldFields.has(field.key)).map((field) => field.label);
      const removedFields = old.fields.filter((field) => !newFields.has(field.key)).map((field) => field.label);
      const changedFields = record.fields.filter((field) => oldFields.has(field.key) && !same(oldFields.get(field.key), field)).map((field) => field.label);
      changed.push({
        op: 'changed',
        kind: recordKind(record),
        path,
        name: record.name,
        ...(old.name === record.name ? {} : { renamedFrom: old.name }),
        ...(addedFields.length === 0 ? {} : { addedFields }),
        ...(removedFields.length === 0 ? {} : { removedFields }),
        ...(changedFields.length === 0 ? {} : { changedFields }),
      });
    }
    const oldTiles = byKey(old.tiles ?? []);
    const newTiles = byKey(record.tiles ?? []);
    for (const tile of record.tiles ?? []) {
      const tilePath = `${path}/card:${tile.key}`;
      const held = oldTiles.get(tile.key);
      if (held === undefined) added.push({ op: 'added', kind: 'card', path: tilePath, name: tile.label, on: record.name });
      else if (!same(held, tile)) changed.push({ op: 'changed', kind: 'card', path: tilePath, name: tile.label, on: record.name });
    }
    for (const tile of old.tiles ?? []) {
      if (!newTiles.has(tile.key)) left.push({ op: 'left', kind: 'card', path: `${path}/card:${tile.key}`, name: tile.label, on: record.name });
    }
  }
  for (const record of before.records) {
    if (!now.has(record.key)) left.push({ op: 'left', kind: recordKind(record), path: `record:${record.key}`, name: record.name });
  }

  /** The keyed parts beside the records: one line each way, worded by `describe`. */
  const keyed = <T extends { key: string }>(
    kind: StudioPartKind,
    prefix: string,
    oldList: readonly T[],
    newList: readonly T[],
    describe: (item: T, draft: StudioDraft) => { name: string; on?: string; to?: string },
  ): void => {
    const oldBy = byKey(oldList);
    const newBy = byKey(newList);
    for (const item of newList) {
      const held = oldBy.get(item.key);
      const words = describe(item, after);
      if (held === undefined) added.push({ op: 'added', kind, path: `${prefix}:${item.key}`, ...words });
      else if (!same(held, item)) changed.push({ op: 'changed', kind, path: `${prefix}:${item.key}`, ...words });
    }
    for (const item of oldList) {
      if (!newBy.has(item.key)) left.push({ op: 'left', kind, path: `${prefix}:${item.key}`, ...describe(item, before) });
    }
  };
  keyed('link', 'link', before.links, after.links, (link, draft) => ({ name: recordName(draft, link.from), to: recordName(draft, link.to) }));
  keyed('board', 'board', before.boardCards, after.boardCards, (card) => ({ name: card.label }));
  keyed('role', 'role', before.roles, after.roles, (role) => ({ name: role.name }));
  keyed('import', 'import', before.imports, after.imports, (plan, draft) => ({ name: plan.sheet, on: recordName(draft, plan.record) }));

  // Sources: only that one changed; the client names a file from its own list.
  const oldSources = new Map(before.sources.map((one) => [one.id, one]));
  const newSources = new Map(after.sources.map((one) => [one.id, one]));
  for (const source of after.sources) {
    if (!same(oldSources.get(source.id), source)) changed.push({ op: 'changed', kind: 'source', path: `source:${source.id}`, name: '' });
  }
  for (const source of before.sources) {
    if (!newSources.has(source.id)) changed.push({ op: 'changed', kind: 'source', path: `source:${source.id}`, name: '' });
  }

  // A gap the person chose to leave.
  const wasLeft = new Set(before.dismissedGaps.map((one) => one.gap));
  for (const { gap } of after.dismissedGaps) {
    // The language picked (9.12) is no part of the setup: nothing to say.
    if (!wasLeft.has(gap) && gap !== LANGUAGE_PICKED_GAP) changed.push({ op: 'changed', kind: 'gap', path: `gap:${gap}`, name: '' });
  }

  // What was decided or assumed, and what cannot be held yet: new ones only.
  const oldNotes = byKey(before.notes);
  for (const note of after.notes) {
    if (!oldNotes.has(note.key) && (note.kind === 'decided' || note.kind === 'assumed')) decided.push({ op: 'decided', path: `note:${note.key}`, text: note.text });
  }
  const oldNotYet = byKey(before.notYet);
  for (const item of after.notYet) {
    if (!oldNotYet.has(item.key)) notYet.push({ op: 'not_yet', path: `not_yet:${item.key}`, name: item.name, fit: item.fit });
  }

  return [...added, ...changed, ...left, ...decided, ...notYet];
};

/** What the canvas rings for a receipt: every line's path. */
export const studioReceiptRing = (lines: readonly StudioReceiptLine[]): string[] =>
  lines.flatMap((line) => ('path' in line ? [line.path] : []));

/** The rebase rule (J2.5): the paths of `mine` that meet any of `theirs`. */
export const studioOverlaps = (mine: readonly string[], theirs: readonly string[]): string[] =>
  mine.filter((path) => theirs.some((other) => studioPathsMeet(path, other)));

/** The number the person sees for a version: its `n`, else its own number (a version stored before 16 Oct). */
export const studioShownVersion = (receipt: Pick<StudioReceipt, 'version' | 'n'>): number => receipt.n ?? receipt.version;
